import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { AgGridEvent, ColDef, GridOptions, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ApiResponse } from 'src/shared/models/api-response.model';
import { ModuleManager } from 'src/shared/models/module-manager';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerModuleSettings } from 'src/shared/models/module-manager/module-manager-settings.models';
import { ClearViewUtils } from 'src/shared/utils';
import { ReportSelectorSettings, ReportingSetData, ReportingSetDefinitionApiModel, ReportingSetFieldType } from '../../../../../shared/models/pipeline-config/reporting-sets.models';
import { channelTypeToIconMap, ReportColumnInfo } from './report.model';
import { ReportService } from './report.service';
import { ChannelTypeCellRendererComponent } from './channel-type-cell-renderer/channel-type-cell-renderer.component';
import { CxonePlayIconCellComponent } from './cxone-play-icon-cell/cxone-play-icon-cell.component';
import { IconDetail } from './channel-type-cell-renderer/channel-type-cell-renderer.model';

@Component({
    selector: 'cv-report',
    templateUrl: './report.component.html',
    styleUrls: ['./report.component.scss']
})
export class ReportComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild('reportWidgetGrid') agGrid: AgGridAngular;

    // AG Grid
    gridOptions: GridOptions = null;
    columnDefs: ColDef[] = [];
    groupColumnDef: ColDef = {};
    private gridReady = false;

    // Data
    isFirstLoad = true;
    noData = false;
    loadingPromise: Promise<ApiResponse<ReportingSetData>> = null;
    private _loadAborter: () => void;
    isAborting = false;
    private data: ReportingSetData;
    private prevData: ReportingSetData;
    dataLoadSuccess = false;
    forceColDefUpdate = false;

    // State
    _stateSaveTimer: any;
    private tableState = null;
    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"

    // Filter
    filterValue = '';
    showFilter = false;

    // Config
    isCXOne = false;

    //For cxone search placeholder localization
    searchPlaceholder = "";
    gridPopulated = false;

    // Fully loaded API Models list (including the Reporting Set Definition objects themselves and wrapper helper properties)
    definitions: ReportingSetDefinitionApiModel[] = [];
    selectedReportingSet: ReportingSetDefinitionApiModel;

    //To show/hide 10,000 rows limit notification
    isLimitNotificationShown = false;
    isLimitNotificationClosed = false;
    limitNotificationMessage = "";
    moduleIdQualityEvaluations = 149;

    private requireDate = false;

    constructor(
        private agGridHelperService: AgGridHelperService,
        private config: CvConfigurationService,
        private datePipe: DatePipe,
        private localizationService: CVLocalizationService,
        private service: ReportService,
    ) {
        this.isCXOne = this.config.isCxOne;
    }

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; };

    async ngOnInit(): Promise<void> {
        const tasks = [
            this.localizationService.GetSection('Report_Widget'),
            this.localizationService.GetSection('Common'),
            this.localizationService.GetSection('Widgets'),
        ];
        await Promise.all(tasks);

        this.searchPlaceholder = this.localizationService.getCachedText("Common", "Search", "Search");
        this.limitNotificationMessage = this.localizationService.Format(
            this.localizationService.getCachedText("Widgets", "Limit_Notification_Message_No_Export", "Displaying the first {0} records. Adjust filters for more precise results."),
            "10,000"
        );


        this.definitions = await this.service.loadReportDefinitions(this.moduleManager.container.moduleId);
        this.updateSelectedReport();

        this.moduleManager.setOptions({
            hasRefresh: true,
            refreshType: null, // turned off auto update
            onSettingsApplied: this.onSettingsApplied.bind(this),
            onRefresh: this.onRefresh.bind(this),
            onExport: format => this.onExport(format),
        });

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        this.moduleManager.customSettingsAdded = true;
        this.moduleManager.maxDateRange = this.moduleManager.loadedStateValues.maxDateRange;

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: stateData => {
                this.tableState = stateData;

                if (this.gridReady) {
                    this.onTableStateLoaded(stateData);
                }
            },
            autoLoad: true
        });

        this.gridOptions = this.agGridHelperService.createGridOptions({
            context: this.moduleManager,

            // grid options
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressContextMenu: false,
            components: {
                channelTypeCellRenderer: ChannelTypeCellRendererComponent,
                cxonePlayIconCellComponent: CxonePlayIconCellComponent
            },

            // grid data
            defaultColDef: {
                minWidth: this.agGridHelperService.defaultMinWidth,
                filter: true,
            },
            columnDefs: [],
            rowData: [],
            autoGroupColumnDef: null,

            // grid events
            onGridReady: event => {
                this.gridReady = true;

                // if updateGrid() was called before grid was ready call it now
                if (!this.gridPopulated)
                    this.updateGrid(this.moduleManager.moduleSettings.reportingSetName);

                // If the tablestate was loaded before grid ready, trigger it now.
                if (this.tableState) {
                    this.onTableStateLoaded(this.tableState);
                }

                this.stateRestoreInProgress = true; // Prevent state save until the widget is "stable"

                this.restoreColumnState(); //Needed to restore columns size saved on the tableState.
                if (!this.tableState) {
                    this.agGridHelperService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the tableState is null.
                    this.updateTableState();
                }

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for events to finish this frame, then allow state saving next frame
            },
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.queueStateSave();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreColumnState();
                }
            },
            onSortChanged: () => this.queueStateSave(),
            overlayNoRowsTemplate: this.config.isCxOne ?
                `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.localizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>` : null,
            onFilterChanged: () => this.queueStateSave(),
            onColumnMoved: () => this.queueStateSave(),
            onColumnPinned: () => this.queueStateSave(),
            onColumnResized: event => {
                if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                    this.queueStateSave();
                }
            },
            onColumnVisible: () => this.queueStateSave(),
            onColumnRowGroupChanged: () => this.queueStateSave(),
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true,
        }, {
            enableCopy: true
        });

        this.gridOptions.context = {
            moduleManager: this.moduleManager,
        };

        if (!this.moduleManager.hasWriteAccess() && !this.config.isCxOne) {
            this.agGridHelperService.setGridReadOnly(this.gridOptions);
        }

        this.menuItemsForTable();
    }

    menuItemsForTable() {
        this.moduleManager.additionalMenuItems = null;
        this.moduleManager.addMenuItem({
            name: this.localizationService.getCachedText('Module_Menu', 'Columns', 'Columns'),
            icon: "columns",
            getEnabled: () => this.agGrid?.api && this.gridOptions.columnDefs.length > 0,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.gridOptions.columnDefs,
            getItemName: i => this.gridOptions.columnDefs[i].headerName,
            isItemChecked: i => {
                if (this.gridReady && !this.tableState) {
                    this.prepareTableState();
                }
                const columnDefinition: ColDef = this.gridOptions.columnDefs[i];

                // Since the index values may be out of order if columns are reordered,
                // find the correct table state by name/field instead of relying on the index.
                const tableStateMatch = this.tableState.columns.find(c => c.name === columnDefinition.field);
                if (tableStateMatch) {
                    columnDefinition.hide = !tableStateMatch.visible;
                }
                return !columnDefinition.hide;
            },
            onCheckClicked: i => this.toggleColumnVisible(i),
        } as IModuleManagerMenuItem);
        if (this.selectedReportingSet?.ReportingSetDefinition.CanExport) {
            this.moduleManager.addMenuItem({
                name: 'Export',
                icon: 'export',
                type: ModuleManagerMenuItemType.SubMenu,
                getEnabled: () => true,
                subItems: [{
                    name: 'Excel',
                    icon: null,
                    click: () => this.moduleManager.container.exportClicked('excel'),
                },{
                    name: 'CSV',
                    icon: null,
                    click: () => this.moduleManager.container.exportClicked('csv'),
                }]
            } as IModuleManagerMenuItem);
        }
    }

    toggleColumnVisible(i) {
        // read visible state from table state (should always be up to date)
        let columnDefinition: ColDef;
        if (this.gridOptions.columnDefs[i]) columnDefinition = this.gridOptions.columnDefs[i];

        // toggle column visibility
        columnDefinition.hide = !columnDefinition.hide;
        this.agGrid?.api.setColumnVisible(columnDefinition.field, !columnDefinition.hide);

        // update table state
        this.prepareTableState();
        this.queueStateSave();
    };

    async onSettingsApplied(settings: ModuleManagerModuleSettings) {
        if (this.moduleManager.moduleSettings.reportingSetName === undefined)
            return;
        this.updateSelectedReport();
        this.menuItemsForTable();
        await this.localizationService.GetSection(
            ClearViewUtils.standardizeLocalizationKey(this.moduleManager.moduleSettings?.reportingSetName)
        );
        this.loadData(false);
    }

    updateSelectedReport() {
        this.selectedReportingSet = this.definitions.find(d => d.ReportingSetNameForReports === this.moduleManager.moduleSettings.reportingSetName);
        let dates = this.selectedReportingSet?.ReportingSetDefinition?.Dates;
        this.requireDate = (!!dates && Object.keys(dates).length > 0) ?? false;
    }

    onRefresh(isAutoRefresh: boolean) {
        this.loadData(isAutoRefresh);
    }

    async loadData(isAutoRefresh: boolean) {
        if (this.loadingPromise) {
            this._loadAborter?.();
            this.isAborting = true;
            this.loadingPromise = null;
            this._loadAborter = null;
        }

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        const { reportingSetName, dateField } = this.moduleManager.moduleSettings;

        if (reportingSetName === undefined || (this.requireDate && dateField === undefined)) {
            if (this.moduleManager.showLoader)
                this.moduleManager.showLoader = false;
            const reportSelectorSettings: ReportSelectorSettings = {
                HasEmpSelector: false,
                EmpSelectorLevels: [],
                HasRecSelector: false,
                RecSelectorLevels: [],
                HasDateRangeSelector: false,
                ExtraElements: []
            }
            this.moduleManager.updateWidgetSettings(reportSelectorSettings);
            this.prevData = null;
            this.data = null;
            this.noData = true;
            this.dataLoadSuccess = !this.noData;
            return;
        } else {
            const response = await this.service.loadData(reportingSetName, dateField, this.moduleKey);
            this.prevData = this.data;
            this.data = response;
            this.noData = this.data.DataRows.length === 0;
            this.dataLoadSuccess = !this.noData;
            this.updateGrid(reportingSetName);
        }

        this.applyFilter();

        this.isFirstLoad = false;

        this.showLimitNotification();

        this.moduleManager.container.isRefreshing = false;
    }

    onExport(format: string) {
        const { reportingSetName, dateField } = this.moduleManager.moduleSettings;
        return this.service.exportData(reportingSetName, dateField, this.moduleKey, format);
    }

    //#region Columns Set Up
    getColumnNames(reportingSetName: string) {
        this.columnDefs = [];
        const columnLabels = this.data.FieldDefinitions.map(val => {
            const localizedColName = this.localizationService.getCachedText(
                ClearViewUtils.standardizeLocalizationKey(reportingSetName),
                ClearViewUtils.standardizeLocalizationKey(val.FieldDisplay),
                val.FieldDisplay
            );
            const reportColInfo: ReportColumnInfo =
                {reportFieldKey: val.FieldName, displayName: localizedColName, dataType: val.FieldType};
            return reportColInfo;
        });
        const details = this.buildColDefs(columnLabels, reportingSetName);
        const updatedDefs = this.updatedColDefs(details);
        return updatedDefs;
    }

    updatedColDefs(details: {newColumnFound: boolean; numMatchedDefs: number}) {
        // update the column defs on the grid only if we need to
        let columnOrderChange = 0;

        if (this.columnDefs?.length === this.gridOptions.columnDefs.length) {
            for (let j = 1; j < this.gridOptions.columnDefs.length; j++) {
                if (this.columnDefs[j].headerName !== this.gridOptions.columnDefs[j].headerName)
                    columnOrderChange++;
            }
        }

        if (details.newColumnFound || details.numMatchedDefs < this.gridOptions.columnDefs.length || columnOrderChange > 0 || this.forceColDefUpdate) {
            this.gridOptions.columnDefs = this.columnDefs;
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('columnDefs', []);
                this.agGrid?.api.setGridOption('columnDefs', this.columnDefs);
                this.agGrid?.api.setAutoGroupColumnDef(this.groupColumnDef);
                this.agGrid?.api.redrawRows();
                if (this.isFirstLoad && this.tableState) {
                    this.onTableStateLoaded(this.tableState);
                }
                if (this.tableState?.filterStates)
                    this.agGrid?.api.setFilterModel(this.tableState.filterStates);
            }
            this.forceColDefUpdate = false;
            return true;
        }
        return false;
    }

    buildColDefs(colLabels: ReportColumnInfo[], reportingSetName: string) {
        /** object with keys of column names and value set to true if it was there before we reset columnDefs */
        const oldColumnDefMarker = {};
        this.gridOptions?.columnDefs?.forEach((colDef: ColDef) => {
            oldColumnDefMarker[colDef.field] = true;
        });
        // tracking variables for seeing if we need to update columns
        let numMatchedDefs = 0;
        let newColumnFound = false;
        this.groupColumnDef = {};
        colLabels.forEach(col => {
            const colDef: ColDef = {
                colId: col.reportFieldKey,
                enableValue: true,
                field: col.reportFieldKey,
                headerName: col.displayName,
                filter: 'agTextColumnFilter',
                valueFormatter: params => {
                    if (params?.data) {
                        if (col.dataType === ReportingSetFieldType.Boolean) {
                            if(params.value === true)
                                return this.localizationService.getCachedText("Common", "True", "True"); 
                            else if (params.value === false)
                                return this.localizationService.getCachedText("Common", "False", "False");
                            return "";
                        }
                        if (col.dataType === ReportingSetFieldType.Time) {
                            return this.formatTime(params, col.reportFieldKey);
                        }
                        if (col.dataType === ReportingSetFieldType.DateTime) {
                            const date = new Date(params.data[col.reportFieldKey]);
                            return this.datePipe.transform(date, CVAngularDateTimeWithSecondsFormat);
                        }
                        if (params.data[col.reportFieldKey] === 0) {
                            return '0'; // Ensure 0 is not blank
                        }
                        if (!params.data[col.reportFieldKey] || !params.data) {
                            return '';
                        }
                        return params.data[col.reportFieldKey];
                    }
                    return ''; // Return empty string if params.data is not defined
                },
                filterValueGetter: params => {
                    if (params?.data) {
                        if (col.dataType === ReportingSetFieldType.Boolean) {
                            return params.data[col.reportFieldKey] ? "True" : "False";
                        }
                        if (!params.data[col.reportFieldKey] || !params.data) {
                            return '';
                        }
                        if (col.dataType === ReportingSetFieldType.Time) {
                            return this.formatTime(params, col.reportFieldKey);
                        }
                        if (col.dataType === ReportingSetFieldType.DateTime) {
                            const date = new Date(params.data[col.reportFieldKey]);
                            return this.datePipe.transform(date, CVAngularDateTimeWithSecondsFormat);
                        }
                        return params.data[col.reportFieldKey];
                    }
                }
            };
            if (col.dataType === ReportingSetFieldType.Double || col.dataType === ReportingSetFieldType.Int ||
                col.dataType === ReportingSetFieldType.Time) {
                colDef.type = 'numberColumn';
            }

            // Change display type of booleans to text
            if (col.dataType === ReportingSetFieldType.Boolean) {
                colDef.cellDataType = 'text';
            }

            // check if this column was in our column defs before
            if (oldColumnDefMarker[colDef.field] === true) {
                numMatchedDefs++;
            } else {
                newColumnFound = true;
            }

            // Disable AG-Grid based grouping for now
            //if (this.selectedReportingSet?.SupportsGrouping && (this.moduleManager.moduleSettings?.groupByRow?.replace("DataField_", "") === colDef.colId)) {
            //    colDef.rowGroup = true;
            //    colDef.hide = true;

            //    this.groupColumnDef.colId = "group";
            //    this.groupColumnDef.headerName = colDef.headerName;
            //    this.groupColumnDef.showRowGroup = colDef.field;
            //    this.forceColDefUpdate = true;
            //}

            this.columnDefs.push(colDef);
        });

        this.addQualityEvaluationsWidgetColumns(reportingSetName);

        return {newColumnFound, numMatchedDefs};
    }

    formatTime(params: ValueFormatterParams | ValueGetterParams, key: string): string {
        let retValue = '';
        let data = params.data[key];

        const day = 60 * 60 * 24;
        const hour = 60 * 60;
        const minute = 60;

        const dayValue = Math.floor(data / day);
        data = data - (dayValue * day);

        const hourValue = Math.floor(data / hour);
        data = data - (hourValue * hour);

        const minValue = Math.floor(data / minute);
        data = data - (minValue * minute);

        const secValue = Math.floor(data);

        if (dayValue === 0) {
            retValue += '00:';
        } else if (dayValue.toString().length === 1) {
            retValue += '0' + dayValue.toString() + ':';
        } else {
            retValue += dayValue.toString() + ':';
        }

        if (hourValue === 0) {
            retValue += '00:';
        } else if (hourValue.toString().length === 1) {
            retValue += '0' + hourValue.toString() + ':';
        } else {
            retValue += hourValue.toString() + ':';
        }

        if (minValue === 0) {
            retValue += '00:';
        } else if (minValue.toString().length === 1) {
            retValue += '0' + minValue.toString() + ':';
        } else {
            retValue += minValue.toString() + ':';
        }

        if (secValue === 0) {
            retValue += '00';
        } else if (secValue.toString().length === 1) {
            retValue += '0' + secValue.toString();
        } else {
            retValue += secValue.toString();
        }

        if (retValue.includes('NaN'))
            retValue = 'N/A';

        return retValue;
    }

    updateGrid(reportingSetName: string) {
        if (!this.data) {
            this.moduleManager.showLoader = false;
            return;
        }

        // if grid is not ready here initial load will be handled in onGridReady
        if (this.agGrid?.api) {
            this.gridPopulated = true;
            if (this.data.DataRows.length <= 10000) {
                const columnsUpdated = this.getColumnNames(reportingSetName);
                this.agGrid?.api.setGridOption('rowData', this.data.DataRows);
                if (columnsUpdated && !this.isFirstLoad)
                    this.sizeColumnsHandleFilter();
            } else {
                this.agGrid?.api.applyTransaction(
                    this.agGridHelperService.createTransaction(this.prevData?.DataRows, this.data.DataRows, this.gridOptions)
                );
            }
            this.prevData = null;
        }
        else {
            this.gridPopulated = false;
        }

        this.moduleManager.showLoader = false;
    }

    sizeColumnsSmart(event: AgGridEvent) {
        // If gridReady, or clientWidth or clientHeight missing, use the generic sizing. Otherwise (most cases), use the 'smart' sizing.
        if (event.type === "gridReady" || !event['clientWidth'] || !event['clientHeight'])
            this.agGridHelperService.sizeColumnsFromApisSmart(this.agGrid?.api);
        else
            this.agGridHelperService.sizeColumnsSmart(event);
    }
    //#endregion

    //#region Filter
    toggleOpenSearch() {
        if (this.showFilter) {
            this.showFilter = false;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.showFilter = true;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    }

    focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.config.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            }
        }, 200);
    }

    applyFilter() {
        if (this.agGrid?.api) {
            this.agGrid?.api?.setGridOption('quickFilterText', this.filterValue.trim());
            if (this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    clearFilter() {
        this.filterValue = '';
        this.applyFilter();
    }
    //#endregion

    //#region Table State
    onTableStateLoaded(stateData) {
        if (!stateData || Object.keys(stateData).length === 0) { // No table state loaded
            this.prepareTableState();
            return;
        }

        this.restoreColumnState();
    }

    prepareTableState() {
        if (!this.agGrid?.api)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid?.api);
        if (tableState !== null) {
            this.tableState = tableState;
        }
    }

    async updateTableState() {
        if (!this.agGrid?.api || this.stateRestoreInProgress)
            return;

        this.prepareTableState();

        // tell module manager to save the state
        if (await this.moduleManager.saveState('tableState'))
            this.moduleManager.requestPublishNeeded();
    };

    restoreColumnState() {
        if (!this.tableState?.columns || !this.agGrid?.api) {
            return;
        }

        this.agGridHelperService.loadGridState(this.agGrid?.api, this.tableState);
    };

    sizeColumnsHandleFilter() {
        if (!this.data) {
            return;
        }

        // helper function to size columns
        this.agGridHelperService.sizeColumnsFromApisSmart(this.agGrid?.api);
        if (this.tableState?.filterStates)
            this.agGrid?.api.setFilterModel(this.tableState.filterStates);
    };

    queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        if (this._stateSaveTimer != null)
            clearTimeout(this._stateSaveTimer);

        this._stateSaveTimer = setTimeout(() => {
            this.updateTableState();
            this._stateSaveTimer = null;
        }, 500);
    }
    //#endregion

    showLimitNotification() {
        if (!this.isLimitNotificationClosed) {
            this.isLimitNotificationShown = this.data.DataRows.length >= 10000;
        }
    }

    closeLimitNotification() {
        this.isLimitNotificationShown = false;
        this.isLimitNotificationClosed = true;
    }

    addQualityEvaluationsWidgetColumns(reportingSetName: string) {
        if (this.moduleManager?.container?.moduleId === this.moduleIdQualityEvaluations) {

            this.columnDefs = this.data?.FieldDefinitions.filter((column: any) => column?.FieldName !== 'CHANNEL_DESCRIPTION').map((column: any) => {
                let updatedColumn: ColDef = {
                    headerName: column.FieldDisplay.replace(/_/g, ' '),
                    field: column.FieldName,
                    filter: 'agTextColumnFilter'
                };

                if (column.FieldType === ReportingSetFieldType.Boolean) {
                    updatedColumn.cellDataType = 'text';
                    updatedColumn.valueFormatter = params => params.value ? this.localizationService.getCachedText("Common", "True", "True") : this.localizationService.getCachedText("Common", "False", "False");
                }
    
                if (column?.FieldName === 'EVALUATED_SEGMENT_CHANNEL_TYPE') {
                    updatedColumn = {
                        ...updatedColumn,
                        cellRenderer: 'channelTypeCellRenderer',
                        cellRendererParams: {
                            iconPath: (data) => this.getIconPath(data)
                        },
                        valueGetter: (params: any) => {
                            return params.data?.CHANNEL_DESCRIPTION || '';
                        }
                    };
                }

                return updatedColumn;
            });

            if (reportingSetName === 'Evaluation Details') {
                this.columnDefs.push({
                    headerName: this.localizationService.getCachedText("Report_Widget", "Play", "Play"),
                    field: 'action',
                    resizable: false,
                    cellRenderer: 'cxonePlayIconCellComponent'
                });
            };
        }
    }

    public getIconPath(data): IconDetail {
        const iconType = channelTypeToIconMap[typeof data['EVALUATED_SEGMENT_CHANNEL_TYPE'] === 'string' ? data['EVALUATED_SEGMENT_CHANNEL_TYPE'] : ''];
        return { iconType };
    }
}
